import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { ADDON_CATALOG, ROLES } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api';

const emptyForm = {
  addon_type: '',
  label: '',
  description: '',
  unit_price: '',
  stripe_price_id: '',
  is_active: true,
  available_to_role_id: ''
};

export default function AddOnCatalogManagementPage() {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ label: '', is_active: '', addon_type: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [roles, setRoles] = useState([]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPaginated = async (endpoint) => {
    let url = endpoint;
    let all = [];
    while (url) {
      const res = await api.get(url);
      const data = res.data;
      if (Array.isArray(data)) { 
        all = data; 
        break; 
      }
      // Handle paginated response
      if (data.results && Array.isArray(data.results)) {
        all = all.concat(data.results);
        url = data.next || null;
      } else {
        // If no results key, treat as single page
        all = data;
        break;
      }
    }
    return all;
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get(ROLES);
      console.log('Roles fetched:', response.data); // Debug log
      setRoles(response.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const data = await fetchPaginated(ADDON_CATALOG);
      setAddons(data);
    } catch (err) {
      console.error('Error fetching addons:', err);
      showSnackbar('Failed to load addons', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchRoles();
  }, []);

  // Debug: Monitor form state changes
  useEffect(() => {
    console.log('Form state changed:', form);
  }, [form]);

  const handleOpen = (item = null) => {
    if (item) {
      const formData = {
        ...item,
        available_to_role_id: item.available_to_role_id || '',
        stripe_price_id: item.stripe_price_id ?? ''
      };
      setForm(formData);
      setEditingId(item.id);
    } else {
      setForm(emptyForm);
      setEditingId(null);
    }
    setDialogOpen(true);
  };

  const handleClose = () => setDialogOpen(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this addon?')) return;
    try {
      await api.delete(`${ADDON_CATALOG}${id}/`);
      showSnackbar('Addon deleted successfully');
      fetchAll();
    } catch (err) {
      console.error('Error deleting addon:', err);
      showSnackbar('Failed to delete addon', 'error');
    }
  };

  const handleSubmit = async () => {
    if (!form.label || !form.addon_type) {
      showSnackbar('Label and type are required', 'error');
      return;
    }
    const payload = {
      ...form,
      stripe_price_id: form.stripe_price_id?.trim() || null
    };
    try {
      if (editingId) {
        await api.put(`${ADDON_CATALOG}${editingId}/`, payload);
        showSnackbar('Addon updated successfully');
      } else {
        await api.post(ADDON_CATALOG, payload);
        showSnackbar('Addon created successfully');
      }
      handleClose();
      fetchAll();
    } catch (err) {
      console.error('Error saving addon:', err);
      const data = err.response?.data;
      let message = 'Operation failed';
      if (data) {
        if (typeof data === 'string') message = data;
        else if (Array.isArray(data)) message = data.map(String).join('. ');
        else if (data.detail) message = data.detail;
        else if (typeof data === 'object') {
          message = Object.entries(data)
            .map(([k, v]) => (Array.isArray(v) ? v.join('. ') : String(v)))
            .join(' ');
        }
      }
      showSnackbar(message, 'error');
    }
  };

  const filteredRows = filterData(addons, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { label: '', is_active: '', addon_type: '' });

  const filterConfig = [
    {
      field: 'label',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by label...'
    },
    {
      field: 'is_active',
      label: 'Active Status',
      type: 'select',
      options: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ]
    },
    {
      field: 'addon_type',
      label: 'Type',
      type: 'select',
      options: [
        { value: '', label: 'All Types' },
        { value: 'self_test', label: 'Self Test' },
        { value: 'assisted_test', label: 'Teacher Assisted Test' },
        { value: 'student_slot', label: 'Student Slot' },
        { value: 'child_slot', label: 'Child Slot' }
      ]
    }
  ];

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      minWidth: 80
    },
    {
      field: 'addon_type',
      headerName: 'Type',
      flex: 1.5,
      renderCell: (params) => (
        <Chip 
          label={params.value.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'label',
      headerName: 'Label',
      flex: 2
    },
    {
      field: 'unit_price',
      headerName: 'Price',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          ${params.value}
        </Typography>
      )
    },
    {
      field: 'stripe_price_id',
      headerName: 'Stripe Price ID',
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="caption" noWrap title={params.value || ''}>
          {params.value || '—'}
        </Typography>
      )
    },
    {
      field: 'role_name',
      headerName: 'Available To',
      flex: 1.5,
      renderCell: (params) => {
        if (params.row.role_name) {
          return (
            <Chip 
              label={params.row.role_name} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          );
        } else {
          return (
            <Chip 
              label="All Roles" 
              size="small" 
              color="default" 
              variant="outlined"
            />
          );
        }
      }
    },
    {
      field: 'is_active',
      headerName: 'Active',
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Yes' : 'No'} 
          size="small" 
          color={params.value ? 'success' : 'default'} 
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add AddOn',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No addons found',
    description: 'Try adjusting your filters or add your first addon.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Add On Catalogue"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredRows.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit AddOn' : 'Add AddOn'}
        </DialogTitle>
        <DialogContent>
          {/* Debug info */}
          {editingId && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Debug: addon_type={form.addon_type}, available_to_role_id={form.available_to_role_id}
              </Typography>
            </Box>
          )}
          <TextField
            label="Label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            fullWidth
            margin="normal"
            autoFocus
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            label="Unit Price"
            type="number"
            value={form.unit_price}
            onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Stripe Price ID"
            placeholder="e.g. price_xxxxxxxxxxxxx"
            value={form.stripe_price_id ?? ''}
            onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })}
            fullWidth
            margin="normal"
            helperText="Optional. Stripe Price ID for this add-on when using Stripe checkout."
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              label="Type"
              value={form.addon_type}
              onChange={(e) => setForm({ ...form, addon_type: e.target.value })}
            >
              <MenuItem value="self_test">Self Test</MenuItem>
              <MenuItem value="student_assisted_test">Student Assisted Test</MenuItem>
              <MenuItem value="teacher_assisted_test">Teacher Assisted Test</MenuItem>
              <MenuItem value="student_slot">Student Slot</MenuItem>
              <MenuItem value="child_slot">Child Slot</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-label">Available To Role</InputLabel>
            <Select
              labelId="role-label"
              label="Available To Role"
              value={form.available_to_role_id}
              onChange={(e) => setForm({ ...form, available_to_role_id: e.target.value })}
            >
              <MenuItem value="">All Roles</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.role_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography sx={{ mr: 2 }}>Active</Typography>
            {/* The Switch component was removed from imports, so this will cause an error.
                Assuming the user intends to re-add it or remove the switch if not needed.
                For now, commenting out the switch as it's not in the new imports. */}
            {/* <Switch 
              checked={form.is_active} 
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            /> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
